import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { SeasonService } from 'src/app/services/season.service';
import { SeasonPassService } from 'src/app/services/season-pass.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { InfoCardComponent } from '../../../shared/info-card/info-card.component';

@Component({
  selector: 'app-season-passes-list',
  templateUrl: './season-passes-list.component.html',
  styleUrls: ['./season-passes-list.component.scss']
})
export class SeasonPassesListComponent implements OnInit {

  business: any;
  passes: any[] = [];
  seasons: any[] = [];
  loaded: boolean = false;
  loadingPage: boolean = true;

  searchText: string = '';
  seasonFilter: number | '' = '';
  seasonDropdownOpen: boolean = false;

  // Estado: se filtra en servidor por `slug` (no por la PK de
  // `season_pass_statuses`, cuyo catálogo solo puede leer un superadmin). Las
  // opciones del desplegable y el contador de pendientes de cobro vienen en el
  // `summary` de la respuesta: el listado llega paginado, así que derivarlos de
  // las filas cargadas solo hablaría de la página visible.
  statusFilter: string = '';
  statusDropdownOpen: boolean = false;
  statusOptions: { slug: string; name: string; total: number }[] = [];
  chargeableCount: number = 0;

  // Scroll infinito: se piden más páginas al llegar al final de la tabla.
  page: number = 1;
  finishScroll: boolean = false;
  loadingMore: boolean = false;
  @ViewChild('containerScroll') containerScroll!: ElementRef;

  // Descarta respuestas de una carga anterior: al cambiar un filtro se pide la
  // página 1 de otra cosa y la petición en vuelo ya no vale.
  private loadGeneration: number = 0;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-group')) {
      this.seasonDropdownOpen = false;
      this.statusDropdownOpen = false;
    }
  }

  private searchTerms = new Subject<string>();

  displayedColumns: string[] = ['actions', 'member_number', 'name', 'season', 'contact', 'registered_at', 'product', 'status'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  @ViewChild(MatSort) sort: MatSort = new MatSort();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private seasonService: SeasonService,
    private seasonPassService: SeasonPassService,
    private apiBusiness: ApiBusinessService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    const qpSeason = this.route.snapshot.queryParams['id_season'];
    if (qpSeason) {
      this.seasonFilter = +qpSeason;
    }
  }

  ngAfterViewInit() {
    this.apiBusiness.changed.subscribe({
      next: (value) => {
        this.business = JSON.parse(localStorage.getItem('business') || '{}');
        if (value) {
          this.loadSeasons();
          this.loadPasses();
        }
      }
    });

    this.searchTerms.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchText = term;
      this.loadPasses();
    });
  }

  loadSeasons() {
    const companyId = this.business?.id;
    if (!companyId) return;
    this.seasonService.getSeasonsByCompany(companyId).subscribe({
      next: (res) => { this.seasons = res.data || []; }
    });
  }

  /**
   * `append` distingue la carga de una página más (scroll) de una carga nueva
   * (primera vez o cambio de filtro), que reinicia el listado.
   */
  loadPasses(append: boolean = false) {
    const companyId = this.business?.id;

    if (!append) {
      this.page = 1;
      this.finishScroll = false;
      this.loadingPage = true;
      this.loadGeneration++;
    }

    if (!companyId) {
      this.passes = [];
      this.dataSource = new MatTableDataSource<any>([]);
      this.statusOptions = [];
      this.chargeableCount = 0;
      this.finishScroll = true;
      this.loaded = true;
      this.loadingPage = false;
      this.loadingMore = false;
      return;
    }

    const filters: any = {};
    if (this.searchText) filters.search = this.searchText;
    if (this.seasonFilter) filters.id_season = this.seasonFilter;
    if (this.statusFilter) filters.status = this.statusFilter;

    const generation = this.loadGeneration;

    this.seasonPassService.getSeasonPassesByCompany(companyId, filters, this.page).subscribe({
      next: (res) => {
        if (generation !== this.loadGeneration) return;
        this.setPasses(res?.data, append);
      },
      error: () => {
        if (generation !== this.loadGeneration) return;
        this.setPasses(null, append);
      }
    });
  }

  /**
   * Vuelca la página recibida. Un error corta el scroll (`finishScroll`) para no
   * quedarse reintentando la misma página cada vez que el usuario mueve la rueda.
   */
  private setPasses(payload: any, append: boolean) {
    // Un backend que todavía no pagina devuelve el array plano en `data` (pasa
    // mientras la API paginada no esté desplegada en el entorno): se trata como
    // página única, para no quedarse pidiendo páginas que no existen.
    const paginated = !!payload && !Array.isArray(payload);
    const rows = (paginated ? payload.data : payload) || [];
    this.passes = append ? this.passes.concat(rows) : rows;
    this.finishScroll = !paginated || payload.current_page >= payload.last_page;

    if (payload?.summary) {
      this.statusOptions = payload.summary.statuses || [];
      this.chargeableCount = payload.summary.chargeable_count || 0;
    } else if (!append) {
      this.statusOptions = [];
      this.chargeableCount = 0;
    }

    this.dataSource = new MatTableDataSource(this.passes);
    this.dataSource.sort = this.sort;
    this.loaded = true;
    this.loadingPage = false;
    this.loadingMore = false;
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('scroll', ['$event'])
  isScrolledInfinityView(event: any) {
    if (this.loadingPage || this.loadingMore || this.finishScroll || !this.containerScroll) return;

    const element = this.containerScroll.nativeElement.getBoundingClientRect();
    const bottomShown = element.bottom <= window.innerHeight
      || event?.target?.offsetHeight + event?.target?.scrollTop >= event?.target?.scrollHeight;

    if (bottomShown) {
      this.page++;
      this.loadingMore = true;
      this.loadPasses(true);
    }
  }

  selectStatusFilter(slug: string) {
    this.statusFilter = slug;
    this.statusDropdownOpen = false;
    this.loadPasses();
  }

  get statusFilterLabel(): string {
    if (!this.statusFilter) return 'Estado';
    return this.statusOptions.find(s => s.slug === this.statusFilter)?.name || 'Estado';
  }

  /**
   * Un abono se puede cobrar mientras no esté activo y no haya terminado
   * (caducado/cancelado). Es el caso de quien se dio de alta con «Cobrar más
   * tarde»: sigue en «Pendiente» y sin ningún pedido creado. El mismo criterio
   * en servidor es SeasonPassRepository::CHARGEABLE_STATUS_SLUGS, que alimenta
   * `chargeableCount`.
   */
  canCharge(pass: any): boolean {
    const slug = pass?.status?.slug;
    return slug === 'pending' || slug === 'inactive';
  }

  /** Sin butaca no hay nivel y sin nivel no hay importe que cobrar. */
  canChargeNow(pass: any): boolean {
    return this.canCharge(pass) && !!pass?.seat;
  }

  openCheckout(pass: any) {
    this.router.navigate(['/panel-control/sports-passes/passes/checkout', pass.id]);
  }

  onSearch(value: string) {
    this.searchTerms.next(value);
  }

  selectSeasonFilter(seasonId: number | '') {
    this.seasonFilter = seasonId;
    this.seasonDropdownOpen = false;
    this.loadPasses();
  }

  get seasonFilterLabel(): string {
    if (this.seasonFilter === '') return 'Temporada';
    const season = this.seasons.find(s => s.id === this.seasonFilter);
    return season ? season.name : 'Temporada';
  }

  clearFilters() {
    this.searchText = '';
    this.seasonFilter = '';
    this.statusFilter = '';
    this.loadPasses();
  }

  openCreate() {
    this.router.navigate(['/panel-control/sports-passes/passes/create']);
  }

  /**
   * Listado de abonados migrados del sistema anterior pendientes de renovar.
   * DESACTIVADO 31-08-2026: ya no era un alta —al renovarlos se conservaba su
   * número de socio y su butaca—, y desde esa fecha entran por el formulario
   * de alta normal. La pantalla sigue en el repo (legacy-renewals-list/), con
   * sus rutas comentadas en app-routing.
   */
  // openRenewals() {
  //   this.router.navigate(['/panel-control/sports-passes/renewals']);
  // }

  openEdit(pass: any) {
    this.router.navigate(['/panel-control/sports-passes/passes/edit', pass.id]);
  }

  deletePass(pass: any) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      disableClose: true,
      data: { icon: true, text: '¿Estás seguro de que quieres eliminar este abono? Esta acción no podrá deshacerse.', btnCancel: true, warning: true }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.seasonPassService.deleteSeasonPass(this.business.id, pass.id).subscribe({
          next: () => {
            this.openInfoCard('Abono eliminado correctamente');
            this.loadPasses();
          },
          error: (err) => {
            this.openInfoCard(this.extractErrorMessage(err, 'Error al eliminar el abono'));
          }
        });
      }
    });
  }

  getStatusBadgeClass(pass: any): string {
    const slug = pass.status?.slug;
    if (slug === 'active') return 'status-badge--active';
    if (slug === 'pending') return 'status-badge--pending';
    if (slug === 'cancelled' || slug === 'expired') return 'status-badge--ended';
    return 'status-badge--inactive';
  }

  getStatusIcon(pass: any): string {
    const slug = pass.status?.slug;
    if (slug === 'active') return 'fa-circle-check';
    if (slug === 'pending') return 'fa-circle-pause';
    if (slug === 'cancelled' || slug === 'expired') return 'fa-circle-xmark';
    return 'fa-circle-exclamation';
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 422 && err?.error?.errors && typeof err.error.errors === 'object' && !Array.isArray(err.error.errors)) {
      const first: any = Object.values(err.error.errors)[0];
      if (Array.isArray(first) && first.length) {
        return first[0];
      }
    }
    return err?.error?.message || fallback;
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }
}
